/**
 * ConfidenceIndicator Component
 *
 * Visual display of confidence level for criterion evaluation results.
 * Shows colored badge with icon and optional percentage score.
 */

import { CheckCircle, AlertTriangle, AlertCircle, HelpCircle } from 'lucide-react';
import type { ConfidenceLevel } from '../../types';

interface ConfidenceIndicatorProps {
  level: ConfidenceLevel;
  score?: number; // 0-1 decimal
}

// Confidence level styling
const CONFIDENCE_STYLES: Record<
  ConfidenceLevel,
  {
    bg: string;
    text: string;
    border: string;
    icon: typeof CheckCircle;
    label: string;
  }
> = {
  high: {
    bg: 'bg-green-50 dark:bg-green-900/20',
    text: 'text-green-700 dark:text-green-300',
    border: 'border-green-200 dark:border-green-800',
    icon: CheckCircle,
    label: 'High Confidence',
  },
  medium: {
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    text: 'text-amber-700 dark:text-amber-300',
    border: 'border-amber-200 dark:border-amber-800',
    icon: AlertTriangle,
    label: 'Medium Confidence',
  },
  low: {
    bg: 'bg-red-50 dark:bg-red-900/20',
    text: 'text-red-700 dark:text-red-300',
    border: 'border-red-200 dark:border-red-800',
    icon: AlertCircle,
    label: 'Low Confidence',
  },
};

// Tooltip text explaining confidence levels
const CONFIDENCE_TOOLTIPS: Record<ConfidenceLevel, string> = {
  high: 'The AI found clear, unambiguous evidence in the source documents supporting this evaluation.',
  medium:
    'The AI found some evidence, but it may be incomplete, indirect, or require interpretation.',
  low: 'The AI found limited or unclear evidence. This result should be carefully verified against the source documents.',
};

export function ConfidenceIndicator({ level, score }: ConfidenceIndicatorProps) {
  const style = CONFIDENCE_STYLES[level];
  const Icon = style.icon;
  const tooltip = CONFIDENCE_TOOLTIPS[level];

  // Format score as percentage if provided
  const percentageText = score !== undefined ? `${Math.round(score * 100)}%` : null;

  return (
    <div
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded border ${style.bg} ${style.border}`}
      title={tooltip}
    >
      <Icon size={14} className={style.text} />
      <span className={`text-xs font-medium ${style.text}`}>
        {percentageText || style.label}
      </span>
      {/* Show tooltip icon for accessibility */}
      <HelpCircle size={12} className={`${style.text} opacity-50`} />
    </div>
  );
}
